import { useNavigationSearch as swUseNavigationSearch } from '@shopware/composables'
import { useShopwareContext, useSessionContext } from '#imports'
import { mapSeoRedirect } from '~/utils/seoRedirect'
import type { Schemas } from '#shopware'

export type SeoUrlResolved = Schemas['SeoUrl'] & {
    /** HTTP redirect status code (301/302) if Shopware indicates a redirect, undefined otherwise */
    redirectCode?: number
    /** Redirect target path or URL, defined when redirectCode is set */
    redirectTarget?: string
    /** Whether the redirect target is an external URL */
    externalTarget?: boolean
}

export function useNavigationSearch() {
    const coreUseNavigationSearch = swUseNavigationSearch()
    const { apiClient } = useShopwareContext()
    const { sessionContext } = useSessionContext()

    /**
     * Resolves a path to its SeoUrl entity.
     * Overrides the Shopware default to also surface seo_redirect extension data
     * (e.g. trailing-slash normalisation) so the caller can handle the redirect.
     */
    async function resolvePath(path: string): Promise<SeoUrlResolved | null> {
        const isHomepage = path === '/'
        const isTechnicalUrl = !isHomepage && (path.startsWith('/navigation/') || path.startsWith('/detail/') || path.startsWith('/landingPage/'))
        const normalizedPath = isHomepage
            ? '/'
            : isTechnicalUrl
                ? path.endsWith('/') ? path.slice(0, -1) : path
                : path.substring(1)

        const { data } = await apiClient.invoke('readSeoUrl post /seo-url', {
            body: {
                filter: [{ type: 'equals', field: isTechnicalUrl ? 'pathInfo' : 'seoPathInfo', value: normalizedPath }],
            }
        })

        const redirect = data.extensions?.seo_redirect
        if (redirect != null) {
            return mapSeoRedirect(redirect) as SeoUrlResolved
        }

        const element = data.elements?.[0]

        if (isHomepage) {
            const categoryId = sessionContext.value?.salesChannel?.navigationCategoryId
            const hreflang = element?.extensions?.hreflang
            return { routeName: 'frontend.navigation.page', foreignKey: categoryId, extensions: { hreflang } } as SeoUrlResolved
        }

        if (element != null) {
            return element as SeoUrlResolved
        }

        return null
    }

    return {
        ...coreUseNavigationSearch,
        resolvePath,
    }
}
